import { UIKit } from "@/shared/ui";
import {
	CatalogAttach,
	catalogSiblings,
	getCatalogItem,
	isLight,
	type CatalogItem,
} from "@/shared/catalog";
import { cableEndCatalogItem, switchesOfLight } from "@/widgets/electrical";
import { useFixturesStore } from "@/shared/store/layers";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { ObjectComments } from "./ObjectComments";

export type PropertiesPanelCatalogItem = CatalogItem;

export const LightProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const pushHistory = useProjectStore((s) => s.pushHistory);

	const updateCeilingItem = useFixturesStore((s) => s.updateCeilingItem);

	// Хелпер для updateFixture в зависимости от каталога

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.Light) {
		const light = layers.electrical.items[selection.id];
		if (!light) return null;
		const info = getCatalogItem(light.ref);
		const alternatives = catalogSiblings(light.ref).filter(
			(candidate) => candidate.attach === CatalogAttach.Ceiling,
		);
		const switches = switchesOfLight(
			layers.electrical,
			layers.wiring.items,
			light.id,
			layers.wiring.connections,
		);

		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					{info?.name ?? "Объект удалён из каталога"}
				</UIKit.Text>
				{info && (
					<div className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
						{info.width} × {info.height} мм, от потолка {info.depth} мм — размеры из каталога
					</div>
				)}
				<UIKit.NumberField
					label="X центра"
					value={light.x}
					step={50}
					onChange={(x) =>
						useProjectStore.getState().recordEdit(() => updateCeilingItem(light.id, { x }))
					}
				/>
				<UIKit.NumberField
					label="Y центра"
					value={light.y}
					step={50}
					onChange={(y) =>
						useProjectStore.getState().recordEdit(() => updateCeilingItem(light.id, { y }))
					}
				/>

				{isLight(info) && (
					<div className="flex flex-col gap-1 rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
						<UIKit.Text as="span">
							Включается:{" "}
							<b className={switches.length > 0 ? "text-ink" : "text-amber-700"}>
								{switches.length > 0
									? switches
											.map(
												(id) => cableEndCatalogItem(layers.electrical, id)?.name ?? "Выключатель",
											)
											.join(", ")
									: "ни одним выключателем"}
							</b>
						</UIKit.Text>
						{switches.length === 0 && (
							<UIKit.Text as="span">
								Добавьте связь выключателя со светильником в разделе освещения.
							</UIKit.Text>
						)}
					</div>
				)}

				<CatalogTypeList
					items={alternatives}
					current={light.ref.item}
					onPick={(picked) =>
						updateCeilingItem(light.id, { ref: { catalog: light.ref.catalog, item: picked } })
					}
				/>

				<ObjectComments kind="light" id={light.id} />
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
					onClick={onDelete}
				>
					{isLight(info) ? "Удалить светильник" : "Удалить объект"}
				</UIKit.Button>
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
